import { useState } from "react"
import { X, AlertCircle, Check, Loader2, Mail } from "lucide-react"

const EUCLID = "'Euclid Circular A', sans-serif"
const MONO   = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
}

const COUNTRY_CODES = [
  { code: "+91", label: "+91 IN" },
  { code: "+1",  label: "+1  US" },
  { code: "+44", label: "+44 GB" },
  { code: "+61", label: "+61 AU" },
  { code: "+49", label: "+49 DE" },
  { code: "+33", label: "+33 FR" },
  { code: "+86", label: "+86 CN" },
  { code: "+81", label: "+81 JP" },
]

const ROLES = [
  { value: "admin",       label: "Admin",       desc: "Full access to all settings and members" },
  { value: "interviewer", label: "Interviewer",  desc: "Can conduct and review interviews" },
  { value: "recruiter",   label: "Recruiter",    desc: "Can manage job listings and applicants" },
]

const inputStyle = {
  width: "100%", height: 36,
  padding: "0 12px",
  border: T.border, borderRadius: T.radius,
  fontSize: 13, fontWeight: 500,
  color: T.text, fontFamily: EUCLID,
  background: T.bg, outline: "none",
  transition: "border-color 0.12s",
  boxSizing: "border-box",
}

const Label = ({ children, optional }) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.07em", color: T.textTertiary, fontFamily: EUCLID }}>
      {children}
    </span>
    {optional && (
      <span style={{ fontSize: 11, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID }}>— optional</span>
    )}
  </div>
)

const AddUserModal = ({ isOpen, onClose, onAddUser, addMemberStatus, isLoading = false }) => {
  const [formData, setFormData] = useState({
    email: "",
    role: "interviewer",
    firstName: "",
    lastName: "",
    phoneNumber: "",
  })
  const [phoneCountryCode, setPhoneCountryCode] = useState("+91")

  const set = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.email.trim()) return
    await onAddUser({
      email: formData.email,
      role: formData.role,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: formData.phoneNumber ? `${phoneCountryCode}${formData.phoneNumber}` : "",
    })
  }

  const handleClose = () => {
    setFormData({ email: "", role: "interviewer", firstName: "", lastName: "", phoneNumber: "" })
    setPhoneCountryCode("+91")
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes aum-spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.35)",
        backdropFilter: "blur(2px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 50, fontFamily: EUCLID,
      }}>
        <div style={{
          background: T.bg,
          width: "100%", maxWidth: 480,
          borderRadius: T.radiusCard,
          border: T.border,
          boxShadow: "0 20px 48px rgba(0,0,0,0.14)",
          display: "flex", flexDirection: "column",
          maxHeight: "92vh", overflow: "hidden",
        }}>

          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "13px 20px",
            borderBottom: T.border,
            background: T.bgMuted,
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#111827", flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 500, color: T.text, fontFamily: EUCLID }}>
                Add Member
              </span>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              style={{
                width: 28, height: 28,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: T.bg, border: T.border,
                borderRadius: T.radius - 1, cursor: "pointer",
                color: T.textTertiary, transition: "all 0.12s", outline: "none",
                opacity: isLoading ? 0.4 : 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#111827"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#111827" }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.textTertiary; e.currentTarget.style.borderColor = "#e5e7eb" }}
            >
              <X size={13} strokeWidth={2} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <div style={{ padding: "20px 20px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 18 }}>

              {/* Email */}
              <div>
                <Label>Email</Label>
                <div style={{ position: "relative" }}>
                  <div style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex", alignItems: "center" }}>
                    <Mail size={13} color="#d1d5db" />
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="name@company.com"
                    required
                    disabled={isLoading}
                    style={{ ...inputStyle, paddingLeft: 32, opacity: isLoading ? 0.5 : 1 }}
                    onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
                    onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb" }}
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <Label>Role</Label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {ROLES.map(({ value, label, desc }) => {
                    const checked = formData.role === value
                    return (
                      <button
                        key={value}
                        type="button"
                        disabled={isLoading}
                        onClick={() => set("role", value)}
                        style={{
                          display: "flex", alignItems: "flex-start", gap: 12,
                          padding: "10px 14px",
                          border: checked ? "0.5px solid #111827" : T.border,
                          borderRadius: T.radius,
                          background: checked ? "#f9f9f7" : T.bg,
                          cursor: isLoading ? "not-allowed" : "pointer",
                          outline: "none", textAlign: "left", fontFamily: EUCLID,
                          transition: "all 0.12s",
                          opacity: isLoading ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => { if (!checked && !isLoading) e.currentTarget.style.borderColor = "#9ca3af" }}
                        onMouseLeave={(e) => { if (!checked) e.currentTarget.style.borderColor = "#e5e7eb" }}
                      >
                        <div style={{
                          marginTop: 2, width: 14, height: 14, flexShrink: 0,
                          borderRadius: "50%",
                          border: checked ? "0.5px solid #111827" : T.border,
                          background: checked ? "#111827" : T.bg,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.12s",
                        }}>
                          {checked && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />}
                        </div>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: checked ? T.text : T.textMuted, fontFamily: EUCLID, margin: 0 }}>{label}</p>
                          <p style={{ fontSize: 11, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID, margin: "2px 0 0" }}>{desc}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Name row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <Label optional>First Name</Label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    placeholder="First"
                    disabled={isLoading}
                    style={{ ...inputStyle, opacity: isLoading ? 0.5 : 1 }}
                    onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
                    onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb" }}
                  />
                </div>
                <div>
                  <Label optional>Last Name</Label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    placeholder="Last"
                    disabled={isLoading}
                    style={{ ...inputStyle, opacity: isLoading ? 0.5 : 1 }}
                    onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
                    onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb" }}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <Label optional>Phone Number</Label>
                <div
                  style={{
                    display: "flex", height: 36,
                    border: T.border, borderRadius: T.radius, overflow: "hidden",
                    transition: "border-color 0.12s", boxSizing: "border-box",
                  }}
                  onFocusCapture={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
                  onBlurCapture={(e)  => { e.currentTarget.style.borderColor = "#e5e7eb" }}
                >
                  <select
                    value={phoneCountryCode}
                    onChange={(e) => setPhoneCountryCode(e.target.value)}
                    disabled={isLoading}
                    style={{
                      height: "100%", paddingLeft: 8, paddingRight: 20,
                      background: T.bgMuted,
                      fontSize: 12, fontWeight: 500, color: T.textMuted, fontFamily: MONO,
                      border: "none", borderRight: T.border,
                      outline: "none", appearance: "none",
                      opacity: isLoading ? 0.5 : 1, cursor: "pointer",
                    }}
                  >
                    {COUNTRY_CODES.map(({ code, label }) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => set("phoneNumber", e.target.value)}
                    placeholder="Phone number"
                    disabled={isLoading}
                    style={{
                      flex: 1, paddingLeft: 12, paddingRight: 12,
                      border: "none", background: T.bg,
                      fontSize: 13, fontWeight: 500, color: T.text, fontFamily: EUCLID,
                      outline: "none", opacity: isLoading ? 0.5 : 1,
                    }}
                  />
                </div>
              </div>

              {/* Status message */}
              {addMemberStatus && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 14px",
                  background: addMemberStatus.type === "error" ? "#fef2f2" : addMemberStatus.type === "success" ? "#f0fdf4" : "#eff6ff",
                  border: `0.5px solid ${addMemberStatus.type === "error" ? "#fecaca" : addMemberStatus.type === "success" ? "#bbf7d0" : "#bfdbfe"}`,
                  borderRadius: T.radius,
                  fontSize: 12, fontWeight: 600,
                  color: addMemberStatus.type === "error" ? "#dc2626" : addMemberStatus.type === "success" ? "#16a34a" : "#2563eb",
                  fontFamily: EUCLID,
                }}>
                  {addMemberStatus.type === "error"   && <AlertCircle size={13} style={{ flexShrink: 0 }} />}
                  {addMemberStatus.type === "success" && <Check size={13} style={{ flexShrink: 0 }} />}
                  {addMemberStatus.type === "loading" && <Loader2 size={13} style={{ flexShrink: 0, animation: "aum-spin 0.8s linear infinite" }} />}
                  {addMemberStatus.message}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "flex-end",
              gap: 8,
              padding: "13px 20px",
              borderTop: T.border, background: T.bgMuted, flexShrink: 0,
            }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  height: 34, padding: "0 14px",
                  border: T.border, borderRadius: T.radius,
                  background: T.bg, color: T.textMuted,
                  fontSize: 12, fontWeight: 600, fontFamily: EUCLID,
                  cursor: "pointer", outline: "none",
                  transition: "border-color 0.12s, color 0.12s",
                  opacity: isLoading ? 0.4 : 1,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.color = T.text }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = T.textMuted }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !formData.email.trim()}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  height: 34, padding: "0 16px",
                  border: "none", borderRadius: T.radius,
                  background: "#111827", color: "#fff",
                  fontSize: 12, fontWeight: 600, fontFamily: EUCLID,
                  cursor: isLoading || !formData.email.trim() ? "not-allowed" : "pointer",
                  outline: "none",
                  opacity: isLoading || !formData.email.trim() ? 0.5 : 1,
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) => { if (!isLoading && formData.email.trim()) e.currentTarget.style.background = "#27272a" }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#111827" }}
              >
                {isLoading && <Loader2 size={12} style={{ animation: "aum-spin 0.8s linear infinite" }} />}
                {isLoading ? "Sending…" : "Send Invite"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default AddUserModal
